import React, {
  useCallback,
  useRef,
  useState,
} from "react";
import "./ProtectedPreview.css";
import AURPageFlipPreview from "./AURPageFlipPreview";
import { useLanguage } from "../shared/LanguageContext";
import { websiteCopy } from "../shared/websiteCopy";

const PREVIEW_FIRST_PAGE = 1;
const PREVIEW_LAST_PAGE = 16;
const PAGE_WIDTH = 550;
const PAGE_HEIGHT = 733;

function assertPreviewPage(page) {
  const value = Number(page);

  if (!Number.isInteger(value)) {
    throw new Error("Invalid AUR preview page.");
  }

  if (value < PREVIEW_FIRST_PAGE || value > PREVIEW_LAST_PAGE) {
    throw new Error("AUR preview page is outside the authorized range.");
  }

  return value;
}

export function nextAURProtectedPreviewPage(currentPage) {
  const page = assertPreviewPage(currentPage);
  return page >= PREVIEW_LAST_PAGE ? PREVIEW_LAST_PAGE : page + 1;
}

export function previousAURProtectedPreviewPage(currentPage) {
  const page = assertPreviewPage(currentPage);
  return page <= PREVIEW_FIRST_PAGE ? PREVIEW_FIRST_PAGE : page - 1;
}

function blockProtectedCopy(event) {
  event.preventDefault();
}

function blockProtectedDrag(event) {
  event.preventDefault();
}

function blockProtectedKey(event) {
  const key = String(event.key || "").toLowerCase();
  const modifier = event.ctrlKey || event.metaKey;

  if (modifier && ["c", "x", "s", "p", "u"].includes(key)) {
    event.preventDefault();
    event.stopPropagation();
  }
}

function preserveViewerScroll(action) {
  const scrollY = window.scrollY;

  action();

  window.requestAnimationFrame(() => {
    window.scrollTo(0, scrollY);

    window.requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  });
}

export default function ProtectedPreview() {
  const [page, setPage] = useState(PREVIEW_FIRST_PAGE);
  const pageFlipControlsRef = useRef(null);
  const { language, t } = useLanguage();
  const copy = websiteCopy[language].preview;

  const goNext = useCallback(() => {
    if (page >= PREVIEW_LAST_PAGE) return;

    preserveViewerScroll(() => {
      const moved = pageFlipControlsRef.current?.next();
      if (!moved) return;
    });
  }, [page]);

  const goPrevious = useCallback(() => {
    if (page <= PREVIEW_FIRST_PAGE) return;

    preserveViewerScroll(() => {
      const moved = pageFlipControlsRef.current?.previous();
      if (!moved) return;
    });
  }, [page]);

  return (
    <section
      className="aur-protected-preview"
      aria-labelledby="aur-protected-preview-title"
      onContextMenu={blockProtectedCopy}
      onCopy={blockProtectedCopy}
      onCut={blockProtectedCopy}
      onDragStart={blockProtectedDrag}
      onKeyDown={blockProtectedKey}
    >
      <div className="aur-protected-preview__heading">
        <h2 id="aur-protected-preview-title">{copy.title}</h2>
        <p>
          {copy.description}<em> {language === "es" ? "AUR — El Espejo de la Mente" : "AUR — The Mirror of the Mind"}</em>.
        </p>
      </div>

      <div className="aur-protected-preview__viewer">
        <div className="aur-page-flip-preview">
          <AURPageFlipPreview
            ref={pageFlipControlsRef}
            currentPage={page}
            onPageChange={setPage}
            firstPage={PREVIEW_FIRST_PAGE}
            lastPage={PREVIEW_LAST_PAGE}
            pageWidth={PAGE_WIDTH}
            pageHeight={PAGE_HEIGHT}
            edition={language === "es" ? "spanish" : "english"}
          />
        </div>
      </div>

      <div
        className="aur-protected-preview__controls"
        aria-label={copy.navigation}
      >
        <button
          type="button"
          onClick={goPrevious}
          disabled={page === PREVIEW_FIRST_PAGE}
        >
          {t("previous")}
        </button>

        <span aria-live="polite">
          {t("page")} {page} {t("of")} {PREVIEW_LAST_PAGE}
        </span>

        <button
          type="button"
          onClick={goNext}
          disabled={page === PREVIEW_LAST_PAGE}
        >
          {t("next")}
        </button>
      </div>

      <p className="aur-protected-preview__boundary">
        {copy.boundary}
      </p>
    </section>
  );
}
